import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TarotCard, allTarotCards, selectRandomCards } from '../data/tarotCards';

interface CircularTarotSpreadProps {
  cardCount?: number;
  onCardSelect?: (card: TarotCard, position: number) => void;
  title?: string;
  spreadType?: 'sim_nao' | 'tres_cartas' | 'livre';
  userName?: string;
}

interface CardPosition {
  card: TarotCard;
  isRevealed: boolean;
  position: number;
}

const CircularTarotSpread: React.FC<CircularTarotSpreadProps> = ({
  cardCount = 78,
  onCardSelect,
  title = "Consulta de Tarô",
  spreadType = 'livre',
  userName = 'Consulente'
}) => {
  const [cards, setCards] = useState<CardPosition[]>([]);
  const [selectedCards, setSelectedCards] = useState<CardPosition[]>([]);
  const [isSpreadComplete, setIsSpreadComplete] = useState(false);
  const [isShuffling, setIsShuffling] = useState(false);

  // Configurações por tipo de consulta
  const getSpreadConfig = () => {
    switch (spreadType) {
      case 'sim_nao':
        return { maxCards: 1, positions: ['Resposta'] };
      case 'tres_cartas':
        return { maxCards: 3, positions: ['Passado', 'Presente', 'Futuro'] };
      default:
        return { maxCards: 78, positions: [] };
    }
  };

  const spreadConfig = getSpreadConfig();

  // Embaralhar cartas
  const shuffleCards = () => {
    setIsShuffling(true);
    
    // Animação de embaralhamento mais elaborada
    const shuffleSteps = 6;
    let currentStep = 0;
    
    const shuffleInterval = setInterval(() => {
      const shuffledCards = [...allTarotCards]
        .sort(() => Math.random() - 0.5)
        .slice(0, cardCount)
        .map((card, index) => ({
          card,
          isRevealed: false,
          position: index
        }));
      
      setCards(shuffledCards);
      currentStep++;
      
      if (currentStep >= shuffleSteps) {
        clearInterval(shuffleInterval);
        setTimeout(() => {
          setSelectedCards([]);
          setIsSpreadComplete(false);
          setIsShuffling(false);
        }, 600);
      }
    }, 200);
  };

  // Inicializar cartas embaralhadas
  useEffect(() => {
    shuffleCards();
  }, [cardCount]);

  const handleCardClick = (clickedCard: CardPosition) => {
    if (clickedCard.isRevealed || isSpreadComplete) return;

    // Verificar se já atingiu o máximo de cartas
    if (selectedCards.length >= spreadConfig.maxCards) return;

    // Revelar a carta
    const updatedCards = cards.map(card =>
      card.position === clickedCard.position
        ? { ...card, isRevealed: true }
        : card
    );
    setCards(updatedCards);

    // Adicionar à lista de cartas selecionadas
    const revealedCard = { ...clickedCard, isRevealed: true };
    const newSelectedCards = [...selectedCards, revealedCard];
    setSelectedCards(newSelectedCards);

    // Verificar se completou o spread
    if (newSelectedCards.length >= spreadConfig.maxCards) {
      setIsSpreadComplete(true);
    }

    // Callback para o componente pai
    if (onCardSelect) {
      onCardSelect(clickedCard.card, clickedCard.position);
    }
  };

  const resetSpread = () => {
    const resetCards = cards.map(card => ({
      ...card,
      isRevealed: false
    }));
    setCards(resetCards);
    setSelectedCards([]);
    setIsSpreadComplete(false);
  };

  return (
    <div className="min-h-screen bg-gradient-mystical text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Título */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-mystical font-bold mb-4">{title}</h1>
          <p className="text-xl text-gray-200">
            {spreadType === 'sim_nao' 
              ? `${userName}, clique em uma carta para sua resposta`
              : spreadType === 'tres_cartas'
              ? `${userName}, selecione 3 cartas para sua leitura`
              : `${userName}, selecione as cartas que desejar`
            }
          </p>
          <p className="text-lg text-gray-300 mt-2">
            Cartas selecionadas: {selectedCards.length}/{spreadConfig.maxCards === 78 ? '∞' : spreadConfig.maxCards}
          </p>
        </div>

        {/* Botão de embaralhar */}
        <div className="text-center mb-8">
          <motion.button
            onClick={shuffleCards}
            disabled={isShuffling}
            className="px-8 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white font-semibold rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            animate={isShuffling ? {
              boxShadow: [
                "0 0 20px rgba(147, 51, 234, 0.5)",
                "0 0 40px rgba(59, 130, 246, 0.8)",
                "0 0 20px rgba(147, 51, 234, 0.5)"
              ]
            } : {}}
            transition={{
              duration: 1,
              repeat: isShuffling ? Infinity : 0,
              ease: "easeInOut"
            }}
          >
            {isShuffling ? (
              <motion.span
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="inline-block mr-2"
              >
                🔄
              </motion.span>
            ) : (
              '🔀 '
            )}
            {isShuffling ? 'Embaralhando...' : 'Embaralhar Cartas'}
          </motion.button>
        </div>

        {/* Monte de cartas no centro */}
        <div className="flex justify-center mb-8">
          <motion.div 
            className="relative w-80 h-96"
            animate={isShuffling ? {
              rotateY: [0, 5, -5, 0],
              scale: [1, 1.02, 0.98, 1]
            } : {}}
            transition={{
              duration: 0.8,
              repeat: isShuffling ? Infinity : 0,
              ease: "easeInOut"
            }}
          >
            {cards.map((cardPos, index) => (
              <motion.div
                key={cardPos.position}
                className={`absolute cursor-pointer ${
                  cardPos.isRevealed ? 'z-50' : 'hover:z-40'
                }`}
                initial={{
                  left: `${Math.random() * 60}px`,
                  top: `${Math.random() * 60}px`,
                  rotate: (Math.random() - 0.5) * 20,
                  scale: 0.9,
                  opacity: 0
                }}
                animate={{
                  left: isShuffling ? `${Math.random() * 80}px` : `${Math.random() * 60}px`,
                  top: isShuffling ? `${Math.random() * 80}px` : `${Math.random() * 60}px`,
                  rotate: isShuffling ? (Math.random() - 0.5) * 40 : (Math.random() - 0.5) * 20,
                  scale: cardPos.isRevealed ? 1.1 : 1,
                  opacity: 1,
                  zIndex: cardPos.isRevealed ? 1000 : cards.length - index
                }}
                whileHover={{
                  scale: cardPos.isRevealed ? 1.15 : 1.05,
                  rotate: cardPos.isRevealed ? 0 : (Math.random() - 0.5) * 10,
                  boxShadow: "0 10px 30px rgba(255, 215, 0, 0.3)",
                  transition: { duration: 0.2 }
                }}
                transition={{
                  duration: isShuffling ? 0.3 : 0.6,
                  ease: "easeInOut",
                  delay: index * 0.05
                }}
                onClick={() => handleCardClick(cardPos)}
              >
                <motion.div 
                  className={`w-32 h-48 rounded-lg border-2 transition-all duration-500 ${
                    cardPos.isRevealed 
                      ? 'border-gold-400 bg-gradient-to-br from-gold-100 to-gold-200 text-mystical-900 shadow-2xl' 
                      : 'border-mystical-400 bg-gradient-to-br from-mystical-700 to-mystical-800 hover:border-gold-400 hover:shadow-lg'
                  }`}
                  animate={cardPos.isRevealed ? {
                    rotateY: [0, 180],
                    scale: [1, 1.1],
                    boxShadow: [
                      "0 4px 15px rgba(0,0,0,0.2)",
                      "0 15px 40px rgba(255, 215, 0, 0.6)"
                    ]
                  } : {}}
                  transition={{
                    duration: 0.8,
                    ease: "easeInOut"
                  }}
                >
                  {cardPos.isRevealed ? (
                    <motion.div
                      className="h-full flex flex-col overflow-hidden rounded-lg"
                      initial={{ opacity: 0, rotateY: 180 }}
                      animate={{ opacity: 1, rotateY: 0 }}
                      transition={{ duration: 0.5, delay: 0.3 }}
                    >
                      {cardPos.card.image ? (
                        <img
                          src={cardPos.card.image}
                          alt={cardPos.card.name}
                          className="w-full flex-1 object-contain"
                        />
                      ) : (
                        <div className="flex-1 flex items-center justify-center bg-mystical-700 text-2xl">
                          🔮
                        </div>
                      )}
                      <div className="bg-gold-600 text-white text-center text-[9px] font-bold px-1 py-0.5 leading-tight">
                        {cardPos.card.name}
                      </div>
                    </motion.div>
                  ) : (
                    <motion.div 
                      className="w-full h-full flex items-center justify-center"
                      animate={isShuffling ? {
                        rotate: [0, 360],
                        scale: [1, 1.2, 1]
                      } : {}}
                      transition={{
                        duration: 1,
                        repeat: isShuffling ? Infinity : 0,
                        ease: "linear"
                      }}
                    >
                      <div className="text-4xl">🔮</div>
                    </motion.div>
                  )}
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </div>

        {/* Botões de controle */}
        <div className="flex justify-center gap-4 mb-8">
          <button
            onClick={resetSpread}
            className="px-6 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 transition-colors"
          >
            🔄 Resetar
          </button>
        </div>

        {/* Mensagem de consulta completa */}
        {isSpreadComplete && (
          <div className="text-center mb-8">
            <div className="bg-gold-600/20 border border-gold-400 rounded-lg p-4 max-w-md mx-auto">
              <p className="text-gold-200 font-semibold">
                ✨ Consulta completa! Veja suas cartas abaixo.
              </p>
            </div>
          </div>
        )}

        {/* Área de cartas selecionadas */}
        {selectedCards.length > 0 && (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
            <h3 className="text-2xl font-mystical font-bold text-center mb-6 text-gold-300">
              Cartas Reveladas ({selectedCards.length}/{spreadConfig.maxCards === 78 ? '∞' : spreadConfig.maxCards})
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {selectedCards.map((cardPos, index) => {
                return (
                  <div key={cardPos.position} className="bg-white/20 rounded-lg p-4">
                    {cardPos.card.image && (
                      <img
                        src={cardPos.card.image}
                        alt={cardPos.card.name}
                        className="w-32 object-contain rounded-lg border border-gold-400/50 mx-auto mb-3"
                      />
                    )}
                    <h4 className="font-bold text-white text-lg mb-2 text-center">
                      {cardPos.card.name}
                    </h4>
                    <p className="text-sm text-gray-200 mb-2 text-center">
                      {cardPos.card.arcana === 'major' ? 'Arcano Maior' : `Arcano Menor - ${cardPos.card.suit}`}
                    </p>
                    
                    {/* Label da posição */}
                    {spreadConfig.positions.length > 0 && (
                      <div className="bg-gold-600/30 rounded px-2 py-1 text-xs font-semibold text-gold-200 mb-2 inline-block">
                        {spreadConfig.positions[index] || `Carta ${index + 1}`}
                      </div>
                    )}
                    
                    <div className="text-sm text-gray-300 mb-2">
                      <strong>Palavras-chave:</strong> {cardPos.card.keywords?.join(', ') || 'N/A'}
                    </div>
                    <div className="text-sm text-gray-300">
                      <>
                        <strong>Significado:</strong> {cardPos.card.meaning?.upright || cardPos.card.meaning || 'Significado não disponível'}
                      </>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Interpretação personalizada */}
            {isSpreadComplete && (
              <div className="mt-6 bg-mystical-800/50 rounded-lg p-4">
                <h4 className="text-xl font-mystical font-bold text-gold-300 mb-3">
                  Interpretação para {userName}
                </h4>
                <div className="text-gray-200 space-y-2">
                  {spreadType === 'sim_nao' && selectedCards.length > 0 && (
                    <div>
                      <>
                        {userName}, sua carta {selectedCards[0].card.name} sugere uma resposta{' '}
                        <strong>
                          {selectedCards[0].card.keywords?.some(k => k.toLowerCase().includes('positiv') || k.toLowerCase().includes('sucesso') || k.toLowerCase().includes('vitória')) ? 'POSITIVA' : 'que requer reflexão'}
                        </strong>
                        . {selectedCards[0].card.meaning.upright}
                      </>
                    </div>
                  )}
                  
                  {spreadType === 'tres_cartas' && selectedCards.length === 3 && (
                    <div>
                      <p className="mb-2">
                        {userName}, suas cartas revelam uma jornada interessante:
                      </p>
                      <div>
                        <>
                          <strong>Passado ({selectedCards[0].card.name}):</strong> {selectedCards[0].card.meaning.upright}
                        </>
                      </div>
                      <div>
                          <>
                            <strong>Presente ({selectedCards[1].card.name}):</strong> {selectedCards[1].card.meaning.upright}
                          </>
                        </div>
                      <div>
                          <>
                            <strong>Futuro ({selectedCards[2].card.name}):</strong> {selectedCards[2].card.meaning.upright}
                          </>
                        </div>
                    </div>
                  )}
                  
                  {spreadType === 'livre' && (
                    <div>
                      {userName}, suas {selectedCards.length} cartas escolhidas formam um padrão único que reflete sua energia atual. 
                      Cada carta representa um aspecto importante da sua jornada espiritual.
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CircularTarotSpread;